import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasHeaderRow, readTables } from "@/office/word/tables";

export const lektionTabellenkopf: Lesson = {
  id: "kopfzeile",
  title: "Die Überschriftenzeile",
  summary: "Warum die erste Zeile mehr ist als nur fetter Text.",
  durationMinutes: 7,
  learningGoals: [
    "Die Überschriftenzeile als Eigenschaft der Tabelle setzen",
    "Verstehen, was Word dadurch zusätzlich leistet",
    "Barrierefreiheit von Tabellen einschätzen",
  ],
  steps: [
    {
      id: "wiederholungszeile",
      title: "Überschriftenzeile statt fetter erster Zeile",
      explanation: [
        "Hier wiederholt sich das Muster aus dem Formatvorlagen-Modul. Die meisten machen " +
          "die erste Zeile einer Tabelle einfach fett. Sieht aus wie eine Kopfzeile – für " +
          "Word ist es aber eine ganz normale Zeile mit fettem Text.",
        "Markierst du sie stattdessen als Überschriftenzeile, passieren drei Dinge: Word " +
          "wiederholt sie automatisch oben auf jeder Folgeseite, wenn die Tabelle über " +
          "einen Seitenumbruch hinausgeht. Screenreader können die Spaltenüberschriften " +
          "vorlesen, sodass Blinde wissen, in welcher Spalte sie sich befinden. Und beim " +
          "Sortieren lässt Word die Kopfzeile stehen, statt sie mit einzusortieren.",
        "Du setzt sie über die Registerkarte „Layout“ → „Überschriften wiederholen“, " +
          "während der Cursor in der ersten Zeile steht.",
      ],
      keyPoints: [
        "Layout → Überschriften wiederholen, Cursor in der ersten Zeile.",
        "Die Zeile wiederholt sich dann auf jeder Folgeseite.",
        "Ohne sie sind Tabellen für Screenreader kaum benutzbar.",
      ],
      task: {
        instruction: "Markiere die erste Zeile deiner Tabelle als Überschriftenzeile.",
        hint: "Cursor in die erste Zeile setzen, dann Layout → „Überschriften wiederholen“.",
        check: async () => {
          const tabellen = await readTables();
          if (tabellen.length === 0) {
            return fail("Im Dokument gibt es noch keine Tabelle.", "Lege zuerst eine Tabelle an.");
          }

          const tabelle = tabellen[0];
          return fromCriteria(
            [
              criterion(
                `Die Tabelle hat eine Überschriftenzeile (gefunden: ${tabelle.headerRowCount})`,
                hasHeaderRow(tabelle),
              ),
              criterion(
                "Die Überschriftenzeile ist gefüllt",
                tabelle.values[0]?.every((zelle) => zelle.length > 0) === true,
              ),
            ],
            {
              success: "Die Kopfzeile ist gesetzt – sie wiederholt sich jetzt auf jeder Seite.",
              failure: "Die Überschriftenzeile ist noch nicht als solche markiert.",
              hint: "Fett allein reicht nicht. Es muss die Schaltfläche „Überschriften wiederholen“ sein.",
            },
          );
        },
      },
    },
    {
      id: "tabellen-nicht-zum-layouten",
      title: "Tabellen sind keine Layoutwerkzeuge",
      explanation: [
        "Ein verbreiteter Missbrauch: Tabellen ohne Rahmen, um Text nebeneinander zu " +
          "stellen – etwa für einen Briefkopf oder zweispaltige Abschnitte.",
        "Das funktioniert optisch, hat aber Folgen. Screenreader kündigen jede Tabelle als " +
          "Datentabelle an und lesen sie Zelle für Zelle vor, was bei einer Layouttabelle " +
          "keinen Sinn ergibt. Beim Export nach HTML oder PDF entstehen kaputte Strukturen. " +
          "Und spätestens beim Umbau des Dokuments wird die verschachtelte Tabelle zur " +
          "Last.",
        "Für Text nebeneinander gibt es passendere Werkzeuge: Spalten unter „Layout“ → " +
          "„Spalten“ für mehrspaltigen Fließtext, Textfelder für frei platzierte Kästen, " +
          "und für Briefköpfe die Kopfzeile mit Tabstopps.",
      ],
      keyPoints: [
        "Tabellen sind für Daten, nicht für Layout.",
        "Mehrspaltiger Text: Layout → Spalten.",
        "Screenreader lesen jede Tabelle als Datentabelle vor.",
      ],
    },
  ],
};
